import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, Upload, FileCode2, Printer, X, Loader2, Info, Plus, Trash2 } from "lucide-react";
import {
  forgetEstimateId,
  loadEstimateFileNames,
  loadEstimateIds,
  rememberEstimateFileName,
  rememberEstimateId,
  saveEstimateIds,
} from "./estimateStorage";

const PENDING_ESTIMATE_STATUSES = ["pending", "starting", "processing"];
const TEMPORARY_ESTIMATE_FILE_NAME = /^estimate-[^/\\]+\.(stl|3mf)$/i;

const getEstimateErrorMessage = (estimate, statusError) => {
  if (statusError) return statusError;
  if (estimate.status === "failed_too_big") {
    return "모델의 사이즈가 320mm x 320mm x 320mm를 초과합니다. 우측 하단의 채널톡으로 문의 부탁드립니다.";
  }
  if (estimate.status === "failed") {
    return "견적 계산에 실패했습니다. 우측 하단의 채널톡으로 문의 부탁드립니다.";
  }
  return null;
};

// 밝은 색상인지 확인하는 함수
const isLightColor = (hexColor) => {
  if (hexColor === "clear") return true;
  // HEX 코드에서 RGB 값 추출
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  // 상대적 밝기 계산 (YIQ 공식 사용)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // 128보다 크면 밝은 색상으로 간주
  return brightness > 128;
};

export const PRINT_MODES = [
  { id: "quality", label: "고해상도", description: "더 정교한 출력, 긴 출력 시간" },
  { id: "default", label: "기본", description: "일반적인 출력 품질" },
  { id: "strength", label: "고강도", description: "높은 강도, 많은 필라멘트 사용" },
];

export const MATERIALS = [
  { id: "pla", label: "PLA", description: "가장 일반적인 소재" },
  { id: "abs", label: "ABS", description: "내구성이 뛰어난 소재" },
  { id: "pc", label: "PC", description: "고강도, 고내열성 소재" },
  { id: "pacf", label: "PA-CF", description: "탄소섬유 강화 소재" },
  { id: "tpu", label: "TPU", description: "부드럽고 탄성 있는 소재" },
];

const PrintEstimator = ({ onEstimateComplete, onShowInfo, user, savedEstimatesContainer = null, renderSavedEstimatesInline = true }) => {
  const [file, setFile] = useState(null);
  const [printMode, setPrintMode] = useState("default");
  const [selectedMaterial, setSelectedMaterial] = useState("pla");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [colors, setColors] = useState({
    pla: [],
    abs: [],
    pacf: [],
    pc: [],
    tpu: [],
  });
  const [selectedColor, setSelectedColor] = useState("");
  const [loadingColors, setLoadingColors] = useState(false);
  const [skipColorSelection, setSkipColorSelection] = useState(false);
  const [expressDelivery, setExpressDelivery] = useState(false);
  const [activeEstimateIds, setActiveEstimateIds] = useState([]);
  const [savedEstimates, setSavedEstimates] = useState([]);
  const [estimateStatusErrors, setEstimateStatusErrors] = useState({});
  const [loadingSavedEstimates, setLoadingSavedEstimates] = useState(true);
  const [deletingEstimateIds, setDeletingEstimateIds] = useState([]);
  const checkingEstimateIdsRef = useRef(new Set());
  const autoOpenEstimateIdsRef = useRef(new Set());
  const forgottenEstimateIdsRef = useRef(new Set());
  const showModalRef = useRef(false);

  const preserveOriginalFileName = useCallback((incomingEstimate, preferredFileName = null) => {
    if (!incomingEstimate?.uuid) return incomingEstimate;

    const storedFileName = loadEstimateFileNames()[incomingEstimate.uuid];
    const incomingFileName = incomingEstimate.file_name;
    const originalFileName = preferredFileName || storedFileName || (TEMPORARY_ESTIMATE_FILE_NAME.test(incomingFileName || "") ? null : incomingFileName);

    if (!originalFileName) return incomingEstimate;
    if (storedFileName !== originalFileName) {
      rememberEstimateFileName(incomingEstimate.uuid, originalFileName);
    }
    return incomingFileName === originalFileName ? incomingEstimate : { ...incomingEstimate, file_name: originalFileName };
  }, []);

  // 견적 상태 확인 함수
  const checkEstimateStatus = useCallback(async (uuid) => {
    if (!uuid || forgottenEstimateIdsRef.current.has(uuid) || checkingEstimateIdsRef.current.has(uuid)) return;

    checkingEstimateIdsRef.current.add(uuid);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/estimate/${encodeURIComponent(uuid)}`);
      const result = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          forgetEstimateId(uuid);
          autoOpenEstimateIdsRef.current.delete(uuid);
          setSavedEstimates((current) => current.filter((item) => item.uuid !== uuid));
          setActiveEstimateIds((current) => current.filter((activeUuid) => activeUuid !== uuid));
          setEstimateStatusErrors((current) => {
            const next = { ...current };
            delete next[uuid];
            return next;
          });
        } else {
          setEstimateStatusErrors((current) => ({ ...current, [uuid]: result.error || result.detail || "견적 상태 확인 중 오류가 발생했습니다." }));
        }
        return;
      }

      if (result.success) {
        if (forgottenEstimateIdsRef.current.has(uuid)) return;
        const updatedEstimate = preserveOriginalFileName(result.data);
        setEstimateStatusErrors((current) => {
          if (!current[uuid]) return current;
          const next = { ...current };
          delete next[uuid];
          return next;
        });
        setSavedEstimates((current) => {
          const exists = current.some((item) => item.uuid === updatedEstimate.uuid);
          return exists ? current.map((item) => (item.uuid === updatedEstimate.uuid ? updatedEstimate : item)) : [updatedEstimate, ...current];
        });

        if (updatedEstimate.status === "completed") {
          rememberEstimateId(updatedEstimate.uuid);
          setActiveEstimateIds((current) => current.filter((activeUuid) => activeUuid !== uuid));
          if (autoOpenEstimateIdsRef.current.delete(uuid) && !showModalRef.current) {
            showModalRef.current = true;
            setEstimate(updatedEstimate);
            setShowModal(true);
          }
        } else if (updatedEstimate.status === "failed") {
          autoOpenEstimateIdsRef.current.delete(uuid);
          setActiveEstimateIds((current) => current.filter((activeUuid) => activeUuid !== uuid));
        } else if (updatedEstimate.status === "failed_too_big") {
          autoOpenEstimateIdsRef.current.delete(uuid);
          setActiveEstimateIds((current) => current.filter((activeUuid) => activeUuid !== uuid));
        }
      } else {
        setEstimateStatusErrors((current) => ({ ...current, [uuid]: result.error || "견적 상태 확인 중 오류가 발생했습니다." }));
      }
    } catch (err) {
      console.error("견적 상태 확인 중 오류 발생:", err);
      setEstimateStatusErrors((current) => ({ ...current, [uuid]: "견적 상태를 확인하기 위해 서버에 연결하지 못했습니다." }));
    } finally {
      checkingEstimateIdsRef.current.delete(uuid);
    }
  }, [preserveOriginalFileName]);

  const fetchColors = useCallback(async (material) => {
    setLoadingColors(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/colors/${material}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setColors((prev) => ({
            ...prev,
            [material]: result.data,
          }));
        } else {
          console.error("Unexpected colors data format:", result);
        }
      } else {
        console.error("Failed to fetch colors");
      }
    } catch (error) {
      console.error("Error fetching colors:", error);
    } finally {
      setLoadingColors(false);
    }
  }, []);

  useEffect(() => {
    fetchColors(selectedMaterial);
    setSkipColorSelection(false);
  }, [fetchColors, selectedMaterial]);

  useEffect(() => {
    if (activeEstimateIds.length === 0) return undefined;
    const checkActiveEstimates = () => {
      activeEstimateIds.forEach((uuid) => checkEstimateStatus(uuid));
    };
    checkActiveEstimates();
    const intervalId = setInterval(checkActiveEstimates, 2000);
    return () => clearInterval(intervalId);
  }, [activeEstimateIds, checkEstimateStatus]);

  useEffect(() => {
    showModalRef.current = showModal;
  }, [showModal]);

  useEffect(() => {
    let cancelled = false;

    const restoreEstimates = async () => {
      setLoadingSavedEstimates(true);
      const token = localStorage.getItem("auth_token");
      let ids = loadEstimateIds();
      let accountEstimates = [];

      if (user && token) {
        await Promise.all(
          ids.map((uuid) =>
            fetch(`${process.env.REACT_APP_API_BASE}/api/estimate/${uuid}/claim`, {
              method: "POST",
              headers: { Authorization: token },
            }).catch(() => null)
          )
        );
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/estimates`, {
            headers: { Authorization: token },
          });
          if (response.ok) accountEstimates = await response.json();
        } catch (restoreError) {
          console.error("계정 견적 복원 오류:", restoreError);
        }
      }

      ids = saveEstimateIds([...accountEstimates.map((item) => item.uuid), ...ids]);
      const accountById = new Map(accountEstimates.map((item) => [item.uuid, item]));
      const localEstimates = await Promise.all(
        ids
          .filter((uuid) => !accountById.has(uuid))
          .map(async (uuid) => {
            try {
              const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/estimate/${uuid}`);
              if (!response.ok) return null;
              const result = await response.json();
              return result.success ? result.data : null;
            } catch {
              return null;
            }
          })
      );

      if (cancelled) return;
      const restored = [...accountEstimates, ...localEstimates.filter(Boolean)]
        .map((item) => preserveOriginalFileName(item))
        .filter((item) => !forgottenEstimateIdsRef.current.has(item.uuid))
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setSavedEstimates((current) => {
        const restoredIds = new Set(restored.map((item) => item.uuid));
        return [...restored, ...current.filter((item) => !restoredIds.has(item.uuid) && !forgottenEstimateIdsRef.current.has(item.uuid))];
      });
      const pendingIds = restored.filter((item) => PENDING_ESTIMATE_STATUSES.includes(item.status)).map((item) => item.uuid);
      setActiveEstimateIds((current) => [...new Set([...current, ...pendingIds])]);
      setLoadingSavedEstimates(false);
    };

    restoreEstimates();
    return () => {
      cancelled = true;
    };
  }, [preserveOriginalFileName, user]);

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile?.name.toLowerCase().endsWith(".stl") || droppedFile?.name.toLowerCase().endsWith(".3mf")) {
      setFile(droppedFile);
      setError(null);
    } else {
      setError("STL 또는 3MF 파일만 업로드 가능합니다.");
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const resetFileInput = () => {
    setFile(null);
    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleEstimate = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append("model_file", file);
    formData.append("print_mode", printMode);
    formData.append("material", selectedMaterial);
    formData.append("express_delivery", String(expressDelivery));

    if (!skipColorSelection) {
      formData.append("color", selectedColor);
    }

    try {
      const token = localStorage.getItem("auth_token");
      const headers = { Accept: "application/json" };
      if (token) headers.Authorization = token;
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/estimate`, {
        method: "POST",
        body: formData,
        headers,
      });
      const result = await response.json();

      if (result.success) {
        const acceptedEstimate = preserveOriginalFileName(result.data, file.name);
        forgottenEstimateIdsRef.current.delete(acceptedEstimate.uuid);
        rememberEstimateId(acceptedEstimate.uuid);
        setSavedEstimates((current) => [acceptedEstimate, ...current.filter((item) => item.uuid !== acceptedEstimate.uuid)]);
        if (PENDING_ESTIMATE_STATUSES.includes(acceptedEstimate.status)) {
          autoOpenEstimateIdsRef.current.add(acceptedEstimate.uuid);
          setActiveEstimateIds((current) => [...new Set([...current, acceptedEstimate.uuid])]);
        } else if (acceptedEstimate.status === "completed") {
          showModalRef.current = true;
          setEstimate(acceptedEstimate);
          setShowModal(true);
        }
        resetFileInput();
      } else {
        setError(result.error || result.detail || "견적 계산에 실패했습니다.");
      }
    } catch (err) {
      setError("서버 연결에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgetEstimate = async (uuid) => {
    if (deletingEstimateIds.includes(uuid)) return;

    setError(null);
    forgottenEstimateIdsRef.current.add(uuid);
    setDeletingEstimateIds((current) => [...current, uuid]);
    try {
      const token = localStorage.getItem("auth_token");
      if (user && token) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/estimates/${encodeURIComponent(uuid)}/forget`, {
          method: "POST",
          headers: { Authorization: token },
        });
        if (!response.ok) throw new Error("계정의 저장된 견적을 삭제하지 못했습니다.");
      }

      forgetEstimateId(uuid);
      autoOpenEstimateIdsRef.current.delete(uuid);
      setActiveEstimateIds((current) => current.filter((activeUuid) => activeUuid !== uuid));
      setSavedEstimates((current) => current.filter((item) => item.uuid !== uuid));
      setEstimateStatusErrors((current) => {
        const next = { ...current };
        delete next[uuid];
        return next;
      });
      if (estimate?.uuid === uuid) {
        showModalRef.current = false;
        setEstimate(null);
        setShowModal(false);
      }
    } catch (forgetError) {
      forgottenEstimateIdsRef.current.delete(uuid);
      setError(forgetError.message || "저장된 견적 삭제 중 오류가 발생했습니다.");
    } finally {
      setDeletingEstimateIds((current) => current.filter((deletingUuid) => deletingUuid !== uuid));
    }
  };

  const handleAddToCart = () => {
    if (estimate && onEstimateComplete) {
      onEstimateComplete(estimate);
      showModalRef.current = false;
      setShowModal(false);
      // 견적 계산 후 파일 초기화
      resetFileInput();
      setEstimate(null);
    }
  };

  const handleMaterialChange = (material) => {
    setSelectedMaterial(material);
    setSelectedColor("");
  };

  const handleColorChange = (color) => {
    setSelectedColor(color);
  };

  const savedEstimatesPanel = (
    <div className={renderSavedEstimatesInline ? "mt-6 border-t pt-5" : "bg-white rounded-xl shadow-lg p-5"}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className={renderSavedEstimatesInline ? "font-semibold text-gray-800" : "text-xl font-bold text-gray-900"}>저장된 견적</h2>
          {!renderSavedEstimatesInline && <p className="mt-1 text-sm text-gray-500">계산 상태를 확인하고 완료된 견적을 장바구니에 담아보세요.</p>}
        </div>
        <span className="shrink-0 text-xs text-gray-400">
          {activeEstimateIds.length > 0 ? `처리 중 ${activeEstimateIds.length}건` : user ? "계정 및 이 브라우저" : "이 브라우저"}
        </span>
      </div>

      {loadingSavedEstimates ? (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> 불러오는 중...
        </div>
      ) : savedEstimates.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 px-4 py-5 text-center">
          <p className="text-sm font-medium text-gray-600">저장된 견적이 없습니다</p>
          <p className="mt-1 text-xs text-gray-400">견적을 요청하면 진행 상태와 결과가 여기에 표시됩니다.</p>
        </div>
      ) : (
        <div data-saved-estimates-grid className={`${renderSavedEstimatesInline ? "space-y-2" : "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"} max-h-[28rem] overflow-y-auto pr-1`}>
          {savedEstimates.map((saved) => {
            const savedError = getEstimateErrorMessage(saved, estimateStatusErrors[saved.uuid]);
            return (
              <div key={saved.uuid} data-estimate-uuid={saved.uuid} className={`flex items-start justify-between gap-3 rounded-lg border px-3 py-3 text-sm ${savedError ? "border-red-200 bg-red-50" : "border-gray-200"}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-700 truncate" title={saved.file_name || saved.uuid}>
                    {saved.file_name || saved.uuid}
                  </p>
                  {savedError ? (
                    <p role="alert" className="mt-1 flex items-start gap-1 text-xs leading-5 text-red-600">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{savedError}</span>
                    </p>
                  ) : saved.status === "completed" ? (
                    <div className="mt-1">
                      <p className="text-xs font-medium text-teal-600">{Number(saved.estimated_cost || 0).toLocaleString()}원</p>
                      {(saved.large_model || saved.express_delivery) && (
                        <p className="mt-0.5 text-[11px] text-gray-500">
                          {[saved.large_model ? "대형 모델" : null, saved.express_delivery ? "빠른 납기" : null].filter(Boolean).join(" · ")}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                      <Loader2 className="h-3 w-3 animate-spin" /> 계산 중
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {saved.status === "completed" && (
                    <button
                      type="button"
                      onClick={() => {
                        showModalRef.current = true;
                        setEstimate(saved);
                        setShowModal(true);
                      }}
                      className="rounded p-1 text-teal-600 hover:bg-teal-50 hover:text-teal-700"
                      title="견적 보기"
                      aria-label={`${saved.file_name || saved.uuid} 견적 보기`}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleForgetEstimate(saved.uuid)}
                    disabled={deletingEstimateIds.includes(saved.uuid)}
                    className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:cursor-wait disabled:opacity-50"
                    title="저장 목록에서 삭제"
                    aria-label={`${saved.file_name || saved.uuid} 저장 목록에서 삭제`}
                  >
                    {deletingEstimateIds.includes(saved.uuid) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 h-full">
      <h2 className="text-2xl font-bold mb-6 text-center text-gray-800 flex items-center justify-center">
        3D 프린팅 견적 계산
        {onShowInfo && (
          <button onClick={onShowInfo} className="p-1 text-gray-400 hover:text-teal-500 transition-colors" title="안내사항 보기">
            <Info className="w-5 h-5" />
          </button>
        )}
      </h2>

      {/* Upload Area */}
      <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-teal-400 border-gray-300 transition-colors bg-gray-50">
        <input type="file" accept=".stl,.3mf" onChange={handleFileChange} className="hidden" id="file-upload" />
        <label htmlFor="file-upload" className="cursor-pointer">
          <Upload className="w-12 h-12 mx-auto text-teal-500 mb-4 opacity-70" />
          {file ? (
            <div className="flex items-center justify-center gap-2 text-teal-600 bg-teal-50 p-3 rounded-lg">
              <FileCode2 className="w-5 h-5" />
              <span className="font-medium">{file.name}</span>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  resetFileInput();
                }}
                className="ml-2 text-gray-500 hover:text-red-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div>
              <p className="text-gray-600 font-medium">파일을 드래그하여 놓거나 클릭하여 선택하세요</p>
              <p className="text-gray-400 text-sm mt-1">.STL 또는 .3MF 파일</p>
            </div>
          )}
        </label>
      </div>

      {/* Print Mode Selection */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-teal-100 text-teal-700 w-6 h-6 inline-flex items-center justify-center rounded-full mr-2 text-sm">1</span>
          프린트 모드 선택
        </h3>
        <div className="grid gap-3">
          {PRINT_MODES.map((mode) => (
            <div
              key={mode.id}
              className={`relative flex items-center p-4 rounded-lg border transition-all hover:shadow-md
                ${printMode === mode.id ? "border-teal-500 bg-teal-50 shadow-sm" : "border-gray-200 hover:border-teal-200"}`}
              onClick={() => setPrintMode(mode.id)}
            >
              <input type="radio" name="print-mode" value={mode.id} checked={printMode === mode.id} onChange={(e) => setPrintMode(e.target.value)} className="w-5 h-5 text-teal-600" />
              <div className="ml-4">
                <label className="font-medium text-gray-900">{mode.label}</label>
                <p className="text-sm text-gray-500 mt-1">{mode.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Material and Color Selection */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <span className="bg-teal-100 text-teal-700 w-6 h-6 inline-flex items-center justify-center rounded-full mr-2 text-sm">2</span>
            소재 및 색상 선택
          </div>
          <div className="relative group">
            <div className="flex items-center bg-teal-50 rounded-full px-3 py-1 border border-teal-200 hover:bg-teal-100 transition-colors cursor-pointer">
              <input type="checkbox" id="skip-color" checked={colors[selectedMaterial]?.length <= 1 ? false : skipColorSelection} disabled={colors[selectedMaterial]?.length <= 1} onChange={() => setSkipColorSelection(!skipColorSelection)} className="w-4 h-4 text-teal-600 mr-2" />
              <label htmlFor="skip-color" className="text-sm text-teal-600 font-medium cursor-pointer">
                색상 미선택
              </label>
            </div>
            <div className="absolute z-10 right-0 mt-2 w-60 bg-white rounded-md shadow-lg p-3 text-sm text-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-200">
              <div className="font-semibold text-teal-600 mb-1">✨ 색상 미선택 혜택</div>
              <p>
                랜덤 컬러로 제작되어 발송되며, 견적가에서 <span className="font-bold text-green-600">10% 할인</span>이 적용됩니다.
              </p>
            </div>
          </div>
        </h3>

        {/* Material Tabs */}
        <div className="flex justify-center space-x-2 mb-4 border-b">
          {MATERIALS.map((material) => (
            <div key={material.id} className="relative group">
              <button onClick={() => handleMaterialChange(material.id)} className={`px-4 py-2 font-medium ${selectedMaterial === material.id ? "border-b-2 border-teal-500 text-teal-600" : "text-gray-500 hover:text-gray-700"}`}>
                {material.label}
              </button>
              <div className="absolute z-10 hidden group-hover:block w-52 p-2 mt-1 text-sm text-gray-600 bg-white rounded-lg shadow-lg border border-gray-200">{material.description}</div>
            </div>
          ))}
        </div>

        {/* Color Selection */}
        {loadingColors ? (
          <div className="flex items-center justify-center p-4">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span>색상 로딩 중...</span>
          </div>
        ) : (
          <div className={`relative ${skipColorSelection ? "opacity-50 pointer-events-none" : ""}`}>
            <div className="flex flex-wrap justify-center gap-4 mb-4 p-2">
              {colors[selectedMaterial]?.map((color) => (
                <button
                  key={color}
                  className={`relative w-9 h-9 rounded-full transition-all transform hover:scale-110 focus:outline-none shadow-md ${selectedColor === color ? "ring-2 ring-offset-2 ring-teal-500 scale-110" : "hover:shadow-lg"}`}
                  style={
                    color === "clear"
                      ? {
                          backgroundImage: 'url("/clear-pattern.svg")',
                          backgroundSize: "100%",
                          backgroundColor: "#fff",
                        }
                      : { backgroundColor: color }
                  }
                  onClick={() => handleColorChange(color)}
                  title={color}
                  disabled={skipColorSelection}
                >
                  {selectedColor === color && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill={isLightColor(color) ? "#000000" : "#FFFFFF"} className="w-5 h-5">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delivery Option */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="bg-teal-100 text-teal-700 w-6 h-6 inline-flex items-center justify-center rounded-full mr-2 text-sm">3</span>
          납기 옵션
        </h3>
        <label className={`flex cursor-pointer items-center justify-between rounded-lg border p-4 transition-all hover:shadow-md ${expressDelivery ? "border-teal-500 bg-teal-50 shadow-sm" : "border-gray-200 hover:border-teal-200"}`}>
          <div className="flex items-center gap-3">
            <input
              id="express-delivery"
              type="checkbox"
              checked={expressDelivery}
              onChange={(event) => setExpressDelivery(event.target.checked)}
              className="h-5 w-5 text-teal-600"
            />
            <div>
              <p className="font-medium text-gray-900">빠른 납기</p>
              <p className="mt-1 text-sm text-gray-500">제작 일정을 우선 배정합니다.</p>
            </div>
          </div>
          <span className="text-sm font-semibold text-teal-600">견적가 +50%</span>
        </label>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-6 p-4 bg-red-50 rounded-lg flex items-center gap-3 text-red-700 border border-red-200">
          <AlertCircle className="w-6 h-6 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Action Button */}
      <div className="mt-8">
        <button onClick={handleEstimate} disabled={!file || !selectedMaterial || (!selectedColor && !skipColorSelection) || loading} className="w-full bg-teal-500 text-white px-6 py-4 rounded-lg font-medium hover:bg-teal-600 disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 transition-colors shadow-md hover:shadow-lg text-lg">
          {loading ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              업로드 중...
            </>
          ) : (
            <>
              <Printer className="w-6 h-6" />
              {activeEstimateIds.length > 0 ? "새 견적 요청하기" : "견적 계산하기"}
            </>
          )}
        </button>
        {activeEstimateIds.length > 0 && <p className="mt-2 text-center text-sm text-gray-500">견적 {activeEstimateIds.length}건 처리 중 · 기다리지 않고 다음 견적을 요청할 수 있습니다.</p>}
      </div>

      {savedEstimatesContainer ? createPortal(savedEstimatesPanel, savedEstimatesContainer) : renderSavedEstimatesInline ? savedEstimatesPanel : null}

      {/* Result Modal */}
      {showModal && estimate && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">견적 계산 결과</h2>
            <div className="space-y-4">
              <div className="flex justify-between border-b pb-3">
                <span className="text-gray-600">파일명</span>
                <span className="font-medium text-gray-900">{estimate.file_name}</span>
              </div>
              <div className="flex justify-between border-b pb-3">
                <span className="text-gray-600">프린트 모드</span>
                <span className="font-medium text-gray-900">{PRINT_MODES.find((mode) => mode.id === estimate.print_mode)?.label}</span>
              </div>
              <div className="flex justify-between border-b pb-3">
                <span className="text-gray-600">필라멘트 색상</span>
                {estimate.color === null ? (
                  <span className="font-medium text-gray-900 flex items-center">
                    <div className="w-5 h-5 rounded-full mr-2 border border-gray-200 bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">?</span>
                    </div>
                    랜덤 컬러 (10% 할인)
                  </span>
                ) : (
                  <span className="font-medium text-gray-900 flex items-center">
                    {estimate.color === "clear" ? (
                      <div
                        className="w-5 h-5 rounded-full mr-2 border border-gray-200 shadow-inner"
                        style={{
                          backgroundImage: 'url("/clear-pattern.svg")',
                          backgroundSize: "100%",
                          backgroundColor: "#fff",
                        }}
                      ></div>
                    ) : (
                      <div className="w-5 h-5 rounded-full mr-2 border border-gray-200 shadow-inner" style={{ backgroundColor: estimate.color }}></div>
                    )}
                    {estimate.color}
                  </span>
                )}
              </div>
              <div className="flex justify-between border-b pb-3">
                <span className="text-gray-600">소재</span>
                <span className="font-medium text-gray-900">{estimate.material === "pacf" ? "PA-CF" : estimate.material.toUpperCase()}</span>
              </div>
              <div className="flex justify-between border-b pb-3">
                <span className="text-gray-600">무게</span>
                <span className="font-medium text-gray-900">{estimate.filament_weight}g</span>
              </div>
              <div className="flex justify-between border-b pb-3">
                <span className="text-gray-600">납기</span>
                <span className="font-medium text-gray-900">{estimate.express_delivery ? "빠른 납기 (+50%)" : "일반 납기"}</span>
              </div>
              {estimate.large_model && (
                <div className="flex justify-between border-b pb-3">
                  <span className="text-gray-600">대형 모델</span>
                  <span className="font-medium text-gray-900">크기 할증 (+50%)</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-teal-600 pt-2">
                <span>견적 금액</span>
                {estimate.color === null ? (
                  <div className="flex flex-col items-end">
                    <span className="text-gray-500 line-through text-sm font-normal">{Math.round(estimate.estimated_cost / 0.9).toLocaleString()}원</span>
                    <span>{estimate.estimated_cost.toLocaleString()}원</span>
                  </div>
                ) : (
                  <span>{estimate.estimated_cost.toLocaleString()}원</span>
                )}
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button
                onClick={() => {
                  resetFileInput();
                  showModalRef.current = false;
                  setEstimate(null);
                  setShowModal(false);
                }}
                className="px-5 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                취소
              </button>
              <button onClick={handleAddToCart} className="px-5 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium transition-colors shadow-md flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                장바구니에 추가
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintEstimator;
